/**
 * @fileoverview Full-screen PIN entry dialog for user switching and lock screen.
 * Uses React portal to render at document.body level, escaping any parent
 * stacking contexts (e.g. sidebar with transform).
 * Supports touch, keyboard, and barcode scanner input.
 */
"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Delete, LogOut } from "lucide-react";
import Image from "next/image";

interface PinEntryDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when PIN is submitted (auto-fires at 4 digits) */
  onPinSubmit: (pin: string) => Promise<{ error?: string }>;
  /** Dialog title */
  title?: string;
  /** Whether to show a cancel/close button */
  showCancel?: boolean;
  /** Called when cancel is clicked */
  onCancel?: () => void;
  /** Whether to show a logout button */
  showLogout?: boolean;
  /** Called when logout is clicked */
  onLogout?: () => void;
}

const PIN_LENGTH = 4;

/**
 * @description Full-screen PIN entry with numpad and circle indicators.
 * Auto-submits when 4 digits are entered. No visible PIN text.
 * Renders via portal to escape sidebar stacking context.
 */
export function PinEntryDialog({
  open,
  onPinSubmit,
  title = "Scan or enter your PIN",
  showCancel = false,
  onCancel,
  showLogout = false,
  onLogout,
}: PinEntryDialogProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Wait for client-side mount for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setPin("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  // Auto-submit when PIN is complete
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !isSubmitting) {
      handleSubmit(pin);
    }
  }, [pin]);

  const handleSubmit = useCallback(
    async (currentPin: string) => {
      setIsSubmitting(true);
      setError(null);

      const result = await onPinSubmit(currentPin);

      if (result?.error) {
        setError(result.error);
        setIsSubmitting(false);
        setTimeout(() => {
          setPin("");
          setError(null);
        }, 1500);
      }
    },
    [onPinSubmit],
  );

  const handleDigit = useCallback(
    (digit: string) => {
      if (isSubmitting) return;
      setError(null);
      setPin((prev) => {
        if (prev.length >= PIN_LENGTH) return prev;
        return prev + digit;
      });
    },
    [isSubmitting],
  );

  const handleBackspace = useCallback(() => {
    if (isSubmitting) return;
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  }, [isSubmitting]);

  // Capture keyboard input (barcode scanners + physical keyboards)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        handleDigit(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        handleBackspace();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleDigit, handleBackspace]);

  const numpadRows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "back"],
  ];

  if (!open || !mounted) return null;

  const content = (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white">
      {/* Logo */}
      <div className="mb-6">
        <Image
          src="/images/podhero-logo.png"
          alt="PODHero"
          width={120}
          height={40}
          className="h-10 w-auto"
          priority
        />
      </div>

      {/* Title */}
      <h2 className="mb-6 text-lg font-semibold text-gray-900 sm:text-xl">{title}</h2>

      {/* PIN circles */}
      <div className="mb-4 flex items-center gap-3 sm:gap-4">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={`h-4 w-4 rounded-full border-2 transition-all duration-150 sm:h-5 sm:w-5 ${
              i < pin.length
                ? "border-blue-500 bg-blue-500"
                : "border-gray-300 bg-transparent"
            }`}
          />
        ))}
      </div>

      {/* Error message */}
      <div className="mb-6 h-6 text-center">
        {error && <p className="text-sm font-medium text-red-500">{error}</p>}
        {isSubmitting && !error && (
          <p className="text-sm text-gray-400">Verifying...</p>
        )}
      </div>

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {numpadRows.flat().map((key, i) => {
          if (key === "") {
            return <div key={i} className="h-14 w-14 sm:h-16 sm:w-16" />;
          }
          if (key === "back") {
            return (
              <button
                key={i}
                type="button"
                onClick={handleBackspace}
                disabled={isSubmitting || pin.length === 0}
                className="flex h-14 w-14 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 sm:h-16 sm:w-16"
              >
                <Delete className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            );
          }
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleDigit(key)}
              disabled={isSubmitting}
              className="flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 text-xl font-light text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 sm:h-16 sm:w-16 sm:text-2xl"
            >
              {key}
            </button>
          );
        })}
      </div>

      {/* Cancel button */}
      {showCancel && onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-6 text-sm text-gray-400 hover:text-gray-600"
        >
          Cancel
        </button>
      )}

      {/* Logout button — bottom right */}
      {showLogout && onLogout && (
        <button
          type="button"
          onClick={onLogout}
          className="fixed bottom-6 right-6 flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      )}
    </div>
  );

  // Portal to document.body to escape sidebar stacking context
  return createPortal(content, document.body);
}
